import { Badge } from 'primereact/badge';
import { TreeNode } from 'primereact/treenode';
import {
  TreeSelect,
  TreeSelectChangeEvent,
  TreeSelectCheckboxSelectionKeyType,
  TreeSelectSelectionKeysType,
} from 'primereact/treeselect';
import { classNames } from 'primereact/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetComponentsParams } from '../../../hooks/api/components';
import { PCFRequestStatus, PCFRequestStatusMap } from '../../enums/pcf-request';
import { expandCompleteStatus } from '../../helpers/expand-complete-status';
import { parseMulti } from '../../helpers/url-parameter-parser';
import styles from './StatusFilter.module.css';

const COMPLETE_STATUS_MAPPING = [PCFRequestStatus.Complete];

interface SelectedNode {
  [key: string]: TreeSelectCheckboxSelectionKeyType;
}

interface StatusFilterProps extends React.ComponentProps<'div'> {
  onStatusFilterChange: (statuses: string[]) => void;
  filters: Partial<GetComponentsParams>;
  allowedStatuses: PCFRequestStatus[];
}

const StatusFilter = ({ onStatusFilterChange, filters, allowedStatuses }: StatusFilterProps) => {
  const { t } = useTranslation();
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState<SelectedNode>({});

  const translatedStatuses = useMemo(() => {
    return Object.entries(PCFRequestStatusMap)
      .filter(([code]) => {
        return allowedStatuses.includes(code as PCFRequestStatus);
      })
      .map(([code]) => {
        return {
          name: t(`pcfRequest.${code}`),
          code,
        };
      });
  }, [PCFRequestStatusMap]);

  // Based on the filter values that initially come from the parent component (from URL actually), set the selected nodes
  useEffect(() => {
    prepareInitialNodeKeys();
  }, []);

  // Convert your flat status to TreeNode array
  const treeNodes: TreeNode[] = translatedStatuses?.map((status) => {
    return {
      key: status.code.toString(),
      label: status.name,
    };
  });

  const prepareInitialNodeKeys = (): void => {
    // Get the array of status keys from parent, i.e. the URL
    const statusKeys = parseMulti(filters.status);

    // Map to store the selected nodes to be set in the state, to show them selected in the tree
    const preSelectedNodeKeys: SelectedNode = {};

    statusKeys.forEach((tagKey: string) => {
      preSelectedNodeKeys[tagKey] = { checked: true, partialChecked: false };
    });
    // Now highlight those nodes in the tree
    setSelectedStatus(preSelectedNodeKeys);
  };

  const handleChange = (e: TreeSelectChangeEvent) => {
    const selectedKeysMap = (e.value as SelectedNode) || {};

    setSelectedStatus(selectedKeysMap);

    // Map back to your original object structure
    const selectedItems: string[] = [];

    Object.keys(selectedKeysMap).forEach((key) => {
      selectedItems.push(key);
    });

    // Only expand the statuses when updating the filters
    const expandedCodes = expandCompleteStatus(selectedItems, COMPLETE_STATUS_MAPPING);

    onStatusFilterChange(expandedCodes);
  };

  const valueTemplate = (nodes: TreeNode[] | TreeNode | null | undefined) => {
    // If nothing is selected, show the placeholder
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return <span className='line-height-2'>{t('componentsPage.status')}</span>;
    }

    // Display the number of selected nodes
    return (
      <div className='flex flex-row gap-1 line-height-2'>
        <span className='text-primary'>{t('componentsPage.status')}:</span>
        <Badge className='flex align-items-center justify-content-center' value={nodes.length} />
      </div>
    );
  };

  // Custom template for items with tick icon
  const nodeTemplate = (node: TreeNode) => {
    return (
      <div className='flex-1 w-full p-2'>
        <div className='flex flex-row justify-content-between align-items-center'>
          <span className={'font-normal break-all'}>{node.label}</span>
        </div>
      </div>
    );
  };

  return (
    <TreeSelect
      value={selectedStatus as TreeSelectSelectionKeysType}
      options={treeNodes}
      selectionMode='multiple'
      valueTemplate={valueTemplate}
      nodeTemplate={nodeTemplate}
      onChange={handleChange}
      onHide={() => {
        setIsOverlayVisible(false);
      }}
      showClear
      filterInputAutoFocus
      onShow={() => {
        setIsOverlayVisible(true);
      }}
      className={classNames(
        styles['status-filter'],
        isOverlayVisible ? styles['status-filter-open'] : styles['status-filter-close'],
        'border-none',
      )}
      pt={{
        root: {
          className: styles['root'],
        },
        label: {
          className: styles['label'],
        },
        closeButton: {
          className: 'hidden',
        },
        wrapper: {
          className: styles['status-filter'],
        },
        header: {
          className: styles['header'],
        },
        clearIcon: {
          className: classNames(
            'text-black-alpha-60',
            {
              hidden: selectedStatus === undefined || Object.keys(selectedStatus).length === 0,
            },
            styles['clear-icon'],
          ),
        },
        trigger: {
          className: classNames({
            'hidden w-0 p-0 overflow-hidden shrink-0':
              selectedStatus && Object.keys(selectedStatus).length > 0,
          }),
        },
      }}
    />
  );
};

export default StatusFilter;
